"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { memo, useCallback, useState } from "react";
import type { DropIdentityMode, DropInteractionParams, DropTimestampLayout } from "../drop.types";
import { DropLocation, hasDropFooter } from "../drop.types";
import { getRankHoverBorderClass, getRankStaticBorderClass } from "../dropRankStyles";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropHeader from "../WaveDropHeader";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropRatings from "../WaveDropRatings";
import WaveDropReactions from "../WaveDropReactions";
import WaveDropReply from "../WaveDropReply";
import WinnerDropBadge from "./WinnerDropBadge";
import { WaveWinnerIdentity } from "@/components/waves/winners/identity/WaveWinnerIdentity";
import { getWinnerVisibleMetadata } from "@/components/waves/winners/identity/winnerIdentity.helpers";
const getRankHoverClass = (rank: number | null): string => {
  return getRankHoverBorderClass(rank);
};
const getDropStyles = (isActiveDrop: boolean) => {
  if (isActiveDrop) {
    return {
      borderColor: "rgba(60,203,127,0.45)",
      boxShadow: "inset 0 0 0 1px rgba(60,203,127,0.2), inset 0 0 24px rgba(60,203,127,0.08)"
    };
  }
  return undefined;
};
interface DefautWinnerDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
const DefaultWinnerDrop = t0 => {
  const $ = _c(104);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    footer,
    showReplyAndQuote,
    identityMode: t1,
    timestampLayout: t2,
    showInteractions: t3,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const showInteractions = t3 === undefined ? true : t3;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const effectiveRank = drop.winning_context?.place ?? drop.rank;
  const decisionTime = drop.winning_context?.decision_time;
  const showIdentity = identityMode !== "hidden";
  let t4;
  if ($[0] !== drop.metadata || $[1] !== drop.wave) {
    t4 = getWinnerVisibleMetadata({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[0] = drop.metadata;
    $[1] = drop.wave;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const visibleMetadata = t4;
  const getBackgroundColorClass = _temp;
  const bgColorClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getBackgroundColorClass(location);
  let t5;
  if ($[3] !== hasTouch || $[4] !== showInteractions) {
    t5 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[3] = hasTouch;
    $[4] = showInteractions;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleLongPress = t5;
  let t6;
  if ($[6] !== activePartIndex || $[7] !== drop || $[8] !== onReply) {
    t6 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[6] = activePartIndex;
    $[7] = drop;
    $[8] = onReply;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleOnReply = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsSlideUp(false);
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleOnAddReaction = t7;
  const t8 = `tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  let t9;
  if ($[11] !== effectiveRank) {
    t9 = getRankStaticBorderClass(effectiveRank);
    $[11] = effectiveRank;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== effectiveRank) {
    t10 = getRankHoverClass(effectiveRank);
    $[13] = effectiveRank;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid ${t9} tw-px-4 tw-py-3 ${bgColorClass} ${t10}`;
  let t12;
  if ($[15] !== isActiveDrop) {
    t12 = getDropStyles(isActiveDrop);
    $[15] = isActiveDrop;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12) {
    t13 = {
      ...t12,
      transition: "box-shadow 0.2s ease, background-color 0.2s ease"
    };
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== drop.reply_to || $[20] !== drop.wave || $[21] !== dropViewDropId || $[22] !== onReplyClick) {
    t14 = drop.reply_to && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[19] = drop.reply_to;
    $[20] = drop.wave;
    $[21] = dropViewDropId;
    $[22] = onReplyClick;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== drop || $[25] !== showIdentity) {
    t15 = showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[24] = drop;
    $[25] = showIdentity;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== activePartIndex || $[28] !== decisionTime || $[29] !== drop || $[30] !== effectiveRank || $[31] !== identityMode || $[32] !== isStorm || $[33] !== showIdentity || $[34] !== timestampLayout) {
    t16 = showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} /> : <WaveDropHeader drop={drop} showWaveInfo={false} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} badge={<WinnerDropBadge rank={effectiveRank} decisionTime={decisionTime ?? null} />} timestampLayout={timestampLayout} />);
    $[27] = activePartIndex;
    $[28] = decisionTime;
    $[29] = drop;
    $[30] = effectiveRank;
    $[31] = identityMode;
    $[32] = isStorm;
    $[33] = showIdentity;
    $[34] = timestampLayout;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== drop.wave || $[37] !== identityMode || $[38] !== showWaveInfo) {
    t17 = identityMode === "default" && showWaveInfo && (() => {
      const waveDetails = drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      };
      const isDirectMessage = waveDetails.chat?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={_temp2} className="tw-mt-0.5 tw-text-xs tw-leading-none tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[36] = drop.wave;
    $[37] = identityMode;
    $[38] = showWaveInfo;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t16 || $[41] !== t17) {
    t18 = <div className="tw-flex tw-flex-col tw-items-start">{t16}{t17}</div>;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const t19 = showIdentity ? "tw-mt-2" : "";
  let t20;
  if ($[43] !== activePartIndex || $[44] !== drop || $[45] !== embedDepth || $[46] !== embedPath || $[47] !== handleLongPress || $[48] !== hasTouch || $[49] !== maxEmbedDepth || $[50] !== onDropContentClick || $[51] !== onQuoteClick || $[52] !== quotePath) {
    t20 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} onLongPress={handleLongPress} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[43] = activePartIndex;
    $[44] = drop;
    $[45] = embedDepth;
    $[46] = embedPath;
    $[47] = handleLongPress;
    $[48] = hasTouch;
    $[49] = maxEmbedDepth;
    $[50] = onDropContentClick;
    $[51] = onQuoteClick;
    $[52] = quotePath;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t19 || $[55] !== t20) {
    t21 = <div className={t19}>{t20}</div>;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t18 || $[58] !== t21) {
    t22 = <div className="tw-flex tw-w-full tw-flex-col">{t18}{t21}</div>;
    $[57] = t18;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t15 || $[61] !== t22) {
    t23 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t15}{t22}</div>;
    $[60] = t15;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== activePartIndex || $[64] !== drop || $[65] !== handleOnReply || $[66] !== isMobile || $[67] !== showInteractions || $[68] !== showReplyAndQuote) {
    t24 = !isMobile && showInteractions && showReplyAndQuote && <div className="tw-absolute tw-right-0 tw-top-1"><WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} /></div>;
    $[63] = activePartIndex;
    $[64] = drop;
    $[65] = handleOnReply;
    $[66] = isMobile;
    $[67] = showInteractions;
    $[68] = showReplyAndQuote;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  const t25 = `${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-flex tw-flex-col tw-gap-2`;
  let t26;
  if ($[70] !== drop) {
    t26 = <WaveWinnerIdentity drop={drop} variant="full" cardVariant="chat" />;
    $[70] = drop;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== visibleMetadata) {
    t27 = visibleMetadata.length > 0 && <WaveDropMetadata metadata={visibleMetadata} />;
    $[72] = visibleMetadata;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== drop || $[75] !== showInteractions) {
    t28 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{!!drop.raters_count && <WaveDropRatings drop={drop} />}<WaveDropReactions drop={drop} /></div>;
    $[74] = drop;
    $[75] = showInteractions;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== t25 || $[78] !== t26 || $[79] !== t27 || $[80] !== t28) {
    t29 = <div className={t25}>{t26}{t27}{t28}</div>;
    $[77] = t25;
    $[78] = t26;
    $[79] = t27;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== footer || $[83] !== showIdentity) {
    t30 = hasDropFooter(footer) && <div className={`${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-pb-1 tw-pt-2`}>{footer}</div>;
    $[82] = footer;
    $[83] = showIdentity;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] !== t11 || $[86] !== t13 || $[87] !== t14 || $[88] !== t23 || $[89] !== t24 || $[90] !== t29 || $[91] !== t30) {
    t31 = <div className={t11} style={t13}>{t14}{t23}{t24}{t29}{t30}</div>;
    $[85] = t11;
    $[86] = t13;
    $[87] = t14;
    $[88] = t23;
    $[89] = t24;
    $[90] = t29;
    $[91] = t30;
    $[92] = t31;
  } else {
    t31 = $[92];
  }
  let t32;
  if ($[93] !== drop || $[94] !== handleOnReply || $[95] !== isSlideUp || $[96] !== longPressTriggered || $[97] !== showInteractions || $[98] !== showReplyAndQuote) {
    t32 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[93] = drop;
    $[94] = handleOnReply;
    $[95] = isSlideUp;
    $[96] = longPressTriggered;
    $[97] = showInteractions;
    $[98] = showReplyAndQuote;
    $[99] = t32;
  } else {
    t32 = $[99];
  }
  let t33;
  if ($[100] !== t31 || $[101] !== t32 || $[102] !== t8) {
    t33 = <div className={t8}>{t31}{t32}</div>;
    $[100] = t31;
    $[101] = t32;
    $[102] = t8;
    $[103] = t33;
  } else {
    t33 = $[103];
  }
  return t33;
};
export default memo(DefaultWinnerDrop);
function _temp(_loc) {
  return "tw-bg-iron-950";
}
function _temp2(e) {
  return e.stopPropagation();
}